// Pure trunk status -> dot color mapping (extracted from VoiceChat for testing).
//
// Inputs come straight off v_gateways — `register`/`enabled` are FusionPBX text
// ('true'/'false') — plus a live `gateway_status` (REGED/NOREG/FAILED/...) that
// the trunk-list API attaches from `sofia status`; '' when FreeSWITCH has no
// entry for the gateway.
//
// Key rule: a register=false trunk (Twilio and other IP-authenticated PSTN
// providers) has nothing to register, so FreeSWITCH lists it as NOREG when it is
// loaded and ready. That is HEALTHY, not a warning — so it must read green, not
// the permanent yellow the old registration-only mapping produced.

export interface TrunkStatusInput {
  gateway_status?: string | null;
  register?: string | boolean | null;
  enabled?: string | boolean | null;
}

export interface TrunkDot {
  cls: string;   // Tailwind bg color class for the dot
  label: string; // human-readable tooltip
}

function isTrue(v: string | boolean | null | undefined): boolean {
  return v === true || String(v).toLowerCase() === 'true';
}
function isFalseFlag(v: string | boolean | null | undefined): boolean {
  return v === false || String(v).toLowerCase() === 'false';
}

export function trunkStatusColor(t: TrunkStatusInput): TrunkDot {
  // Explicitly disabled -> neutral grey, regardless of any stale FS status.
  if (isFalseFlag(t.enabled)) return { cls: 'bg-textSecondary', label: 'Disabled' };

  const s = String(t.gateway_status || '').toUpperCase();

  if (!isTrue(t.register)) {
    // IP-authenticated trunk: nothing to register. Loaded (NOREG/REGED) = up;
    // only an explicit FAILED is a problem; no FS entry = unknown.
    if (s === 'FAILED') return { cls: 'bg-danger', label: 'Failed' };
    if (s === 'NOREG' || s === 'REGED') return { cls: 'bg-success', label: 'Up (IP auth)' };
    return { cls: 'bg-warning', label: s || 'Not loaded' };
  }

  // Registering trunk: registration IS the health signal.
  if (s === 'REGED') return { cls: 'bg-success', label: 'Registered' };
  if (s === 'NOREG') return { cls: 'bg-danger', label: 'Not registered' };
  if (s === 'FAILED') return { cls: 'bg-danger', label: 'Failed' };
  return { cls: 'bg-warning', label: s || 'Unknown' };
}
